import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Delete, Edit, MenuBook, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  PRODUCT_CODE_OPTIONS,
  createMasterMenu,
  deleteMasterMenu,
  ensureMasterMenus,
  fetchMasterMenus,
  setMasterMenuActive,
  updateMasterMenu,
} from '../services/rolesMenuApi';

const isCdxUser = () => {
  try {
    return localStorage.getItem('cdx_user') === 'true';
  } catch {
    return false;
  }
};

const emptyForm = {
  menuCode: '',
  menuName: '',
  parentMenuId: '',
  menuPath: '',
  displayOrder: 0,
  productCode: 'HUB',
  licenseRequired: true,
  platformOnly: false,
  active: true,
};

/**
 * CDX admin — manage global.master_menu (no code seed).
 */
const MasterMenusAdmin = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showInactive, setShowInactive] = useState(true);

  const load = useCallback(async ({ runEnsure = false } = {}) => {
    setLoading(true);
    try {
      // Ensure only when needed — calling it after every save used to wipe edited names
      if (runEnsure) {
        await ensureMasterMenus().catch(() => null);
      }
      const data = await fetchMasterMenus({
        includePlatformOnly: true,
        includeInactive: showInactive,
      });
      setRows(
        data.map((m) => ({
          id: m.menuId,
          ...m,
        }))
      );
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load master menus');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    load({ runEnsure: true });
  }, [load]);

  const parentOptions = useMemo(
    () =>
      rows
        .filter((r) => !editingId || r.id !== editingId)
        .filter((r) => r.active !== false)
        // Only root/section menus as parents — not leaf items from the same grid
        .filter((r) => !r.parentMenuId)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [rows, editingId]
  );

  const nameById = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      map[r.id] = r.menuName;
    });
    return map;
  }, [rows]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      menuCode: row.menuCode || '',
      menuName: row.menuName || '',
      parentMenuId: row.parentMenuId || '',
      menuPath: row.menuPath || '',
      displayOrder: row.displayOrder ?? 0,
      productCode: row.productCode || '',
      licenseRequired: row.licenseRequired !== false,
      platformOnly: !!row.platformOnly,
      active: row.active !== false,
    });
    setDialogOpen(true);
  };

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSave = async () => {
    if (!form.menuCode?.trim()) {
      toast.error('Menu code is required');
      return;
    }
    if (!form.menuName?.trim()) {
      toast.error('Menu name is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        menuCode: form.menuCode.trim(),
        menuName: form.menuName.trim(),
        parentMenuId: form.parentMenuId || null,
        menuPath: form.menuPath?.trim() || null,
        displayOrder: Number(form.displayOrder) || 0,
        productCode: form.productCode || null,
        licenseRequired: !!form.licenseRequired,
        platformOnly: !!form.platformOnly,
        active: !!form.active,
      };
      if (editingId) {
        await updateMasterMenu(editingId, payload);
        toast.success('Menu updated');
      } else {
        await createMasterMenu(payload);
        toast.success('Menu created');
      }
      setDialogOpen(false);
      await load({ runEnsure: false });
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (row) => {
    try {
      await setMasterMenuActive(row.id, !row.active);
      toast.success(row.active ? 'Menu deactivated' : 'Menu activated');
      await load({ runEnsure: false });
    } catch (e) {
      toast.error(e.message || 'Failed to update');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.menuName}"? Prefer deactivate if tenants use it.`)) return;
    try {
      await deleteMasterMenu(row.id);
      toast.success('Menu deleted');
      await load({ runEnsure: false });
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const columns = useMemo(
    () => [
      { field: 'menuCode', headerName: 'Code', width: 160 },
      { field: 'menuName', headerName: 'Name', flex: 1, minWidth: 140 },
      {
        field: 'parentMenuId',
        headerName: 'Parent',
        width: 140,
        valueGetter: (value) => nameById[value] || (value ? '—' : '(root)'),
      },
      { field: 'menuPath', headerName: 'Path', width: 180 },
      { field: 'productCode', headerName: 'Product', width: 110 },
      { field: 'displayOrder', headerName: 'Order', width: 70 },
      {
        field: 'platformOnly',
        headerName: 'CDX only',
        width: 90,
        renderCell: (p) => (p.value ? <Chip size="small" label="Yes" /> : '—'),
      },
      {
        field: 'active',
        headerName: 'Active',
        width: 90,
        renderCell: (p) => (
          <Chip size="small" color={p.value ? 'success' : 'default'} label={p.value ? 'Yes' : 'No'} />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(params.row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={params.row.active ? 'Deactivate' : 'Activate'}>
              <IconButton size="small" onClick={() => handleToggleActive(params.row)}>
                <MenuBook fontSize="small" color={params.row.active ? 'action' : 'disabled'} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => handleDelete(params.row)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [nameById]
  );

  if (!isCdxUser()) {
    return (
      <Box p={3}>
        <Typography color="error">Master Menus is available to CDX users only.</Typography>
      </Box>
    );
  }

  return (
    <Box p={2} sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <MenuBook color="primary" />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Master Menus
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Global menu catalog. Assign to tenants from Tenants → Menus.
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label="Show inactive"
          />
          <Button
            startIcon={<Refresh />}
            variant="outlined"
            onClick={() => load({ runEnsure: false })}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
            Add menu
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableRowSelectionOnClick
          onRowDoubleClick={(p) => openEdit(p.row)}
          sx={{ border: 'none' }}
        />
      </Box>

      <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit menu' : 'Add menu'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Menu code"
                value={form.menuCode}
                onChange={(e) => setField('menuCode', e.target.value)}
                helperText="Unique, e.g. SYNC_FLOW"
                disabled={!!editingId}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Menu name"
                value={form.menuName}
                onChange={(e) => setField('menuName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Parent</InputLabel>
                <Select
                  label="Parent"
                  value={form.parentMenuId || ''}
                  onChange={(e) => setField('parentMenuId', e.target.value)}
                >
                  <MenuItem value="">(root / section)</MenuItem>
                  {parentOptions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.menuName} ({p.menuCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {parentOptions.length === 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  No section parents yet — save a root menu first, then add children under it.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Product</InputLabel>
                <Select
                  label="Product"
                  value={form.productCode || ''}
                  onChange={(e) => setField('productCode', e.target.value)}
                >
                  {PRODUCT_CODE_OPTIONS.map((p) => (
                    <MenuItem key={p.code} value={p.code}>
                      {p.name} ({p.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Route path"
                value={form.menuPath}
                onChange={(e) => setField('menuPath', e.target.value)}
                helperText="e.g. /sync-flow (leave empty for section headers)"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Display order"
                value={form.displayOrder}
                onChange={(e) => setField('displayOrder', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.licenseRequired}
                    onChange={(e) => setField('licenseRequired', e.target.checked)}
                  />
                }
                label="License required"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.platformOnly}
                    onChange={(e) => setField('platformOnly', e.target.checked)}
                  />
                }
                label="CDX / platform only"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch checked={form.active} onChange={(e) => setField('active', e.target.checked)} />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterMenusAdmin;
